import type { EvidenceGraphNode, EvidenceGraphView, EvidenceSource } from '@knowyet/sdk/evidence';
export type Edge = EvidenceGraphView['edges'][number];
export interface Exploration {
  snapshot?: string;
  nodes: Map<string, EvidenceGraphNode>;
  edges: Map<string, Edge>;
  positions: Map<string, { x: number; y: number }>;
}
export const emptyExploration = (): Exploration => ({ nodes: new Map(), edges: new Map(), positions: new Map() });
export const edgeKey = (e: Edge) => JSON.stringify([e.from, e.to, e.kind, e.role ?? '']);
/** Preserve visited nodes and positions. Only server-returned edges are added. */
export function mergeExploration(previous: Exploration, page: Pick<EvidenceGraphView, 'snapshot_id' | 'nodes' | 'edges'>, anchor?: string): Exploration {
  if (previous.snapshot && previous.snapshot !== page.snapshot_id) throw Error('材料版本已变化，请重新查询图谱。');
  const result: Exploration = { snapshot: page.snapshot_id, nodes: new Map(previous.nodes), edges: new Map(previous.edges), positions: new Map(previous.positions) };
  const occupied = new Set([...result.positions.values()].map(p => p.x + ',' + p.y));
  const origin = anchor && result.positions.get(anchor);
  const place = () => {
    if (origin) {
      for (let radius = 1; radius <= result.nodes.size + 2; radius++)
        for (let dy = -radius; dy <= radius; dy++)
          for (let dx = -radius; dx <= radius; dx++) {
            if (Math.max(Math.abs(dx), Math.abs(dy)) !== radius) continue;
            const p = { x: origin.x + dx * 240, y: origin.y + dy * 104 };
            if (!occupied.has(p.x + ',' + p.y)) return p;
          }
    }
    for (let i = 0; ; i++) {
      const p = { x: 120 + i % 4 * 240, y: 56 + Math.floor(i / 4) * 104 };
      if (!occupied.has(p.x + ',' + p.y)) return p;
    }
  };
  for (const node of page.nodes) {
    result.nodes.set(node.id, node);
    if (!result.positions.has(node.id)) {
      const p = place(); result.positions.set(node.id, p); occupied.add(p.x + ',' + p.y);
    }
  }
  for (const edge of page.edges) {
    if (!result.nodes.has(edge.from) || !result.nodes.has(edge.to)) throw Error('关系的端点不在当前图中。');
    result.edges.set(edgeKey(edge), edge);
  }
  return result;
}
export function groupSources(sources: EvidenceSource[]): EvidenceSource[] {
  const grouped = new Map<string, EvidenceSource>();
  for (const source of sources) {
    const previous = grouped.get(source.original.id);
    const locations = [...new Map([...(previous?.locations ?? []), ...source.locations].map(l => [JSON.stringify(l), l])).values()];
    grouped.set(source.original.id, { ...(previous ?? source), locations });
  }
  return [...grouped.values()];
}
/** A workspace admits one reader at a time; failures must not poison the queue. */
export class GraphReadQueue {
  private tail: Promise<unknown> = Promise.resolve();
  private closed = false;
  run<T>(read: () => Promise<T>): Promise<T> {
    const next = this.tail.then(() => {
      if (this.closed) throw Error('图浏览已关闭');
      return read();
    });
    this.tail = next.catch(() => {}); return next;
  }
  close() { this.closed = true; }
}
